// ── Lobby hotspot / element schema translation helpers ────────────────────────
// These convert between the DB wire format (action/config objects) and the
// in-memory React format (hotspotType / hotspotAction) used by the canvas.

export function deserializeLobbyElement(el) {
  if (el.elementType === 'exhibitor-grid') {
    const lobbyId = el.config?.lobbyId || el.meta?.lobbyId || null;
    return {
      ...el,
      hotspotType: 'exhibitor-grid',
      hotspotAction: { exhibitionId: el.config?.exhibitionId || null, lobbyId },
      shape: 'rect',
      points: el.points || [],
      meta: { ...(el.meta || {}), lobbyId },
      isActive: el.isActive !== false,
    };
  }
  if (el.elementType === 'livestream-chat') {
    return {
      ...el,
      hotspotType: 'livestream-chat',
      hotspotAction: { channelId: el.config?.channelId || null, chatTitle: el.config?.chatTitle || 'Livestream Chat' },
      shape: 'rect',
      points: el.points || [],
      meta: el.meta || {},
      isActive: el.isActive !== false,
    };
  }
  return {
    ...el,
    hotspotType: el.elementType || 'stream',
    hotspotAction: {
      streamType:    el.config?.streamType    || 'iframe',
      streamUrl:     el.config?.streamUrl     || '',
      viewerCount:   el.config?.viewerCount   || 0,
      hideUI:        el.config?.hideUI        || false,
      muxStreamId:   el.config?.muxStreamId   || null,
      muxStreamKey:  el.config?.muxStreamKey  || null,
      muxRtmpUrl:    el.config?.muxRtmpUrl    || null,
      muxPlaybackId: el.config?.muxPlaybackId || null,
      muxStatus:     el.config?.muxStatus     || null,
    },
    shape: 'rect',
    points: el.points || [],
    meta: el.meta || {},
    isActive: el.isActive !== false,
  };
}

export function serializeLobbyElement(h) {
  if (h.hotspotType === 'exhibitor-grid') {
    const lobbyId = h.hotspotAction?.lobbyId || h.meta?.lobbyId || null;
    return {
      id: h.id, label: h.label || 'Exhibitor Grid', elementType: 'exhibitor-grid',
      points: h.points || [], meta: { ...(h.meta || {}), lobbyId },
      config: { exhibitionId: h.hotspotAction?.exhibitionId || null, lobbyId },
      responsive: h.responsive || undefined,
      order: h.order ?? 0, isActive: h.isActive !== false,
    };
  }
  if (h.hotspotType === 'livestream-chat') {
    return {
      id: h.id, label: h.label || 'Livestream Chat', elementType: 'livestream-chat',
      points: h.points || [], meta: { ...(h.meta || {}), side: h.meta?.side || 'right' },
      config: { channelId: h.hotspotAction?.channelId || null, chatTitle: h.hotspotAction?.chatTitle || 'Livestream Chat' },
      responsive: h.responsive || undefined,
      order: h.order ?? 0, isActive: h.isActive !== false,
    };
  }
  const _a = h.hotspotAction || {};
  return {
    id: h.id, label: h.label || 'Livestream Player', elementType: 'stream',
    points: h.points || [], meta: h.meta || {},
    config: {
      streamType:    _a.streamType    || 'iframe',
      streamUrl:     _a.streamUrl     || '',
      viewerCount:   _a.viewerCount   || 0,
      hideUI:        _a.hideUI        || false,
      muxStreamId:   _a.muxStreamId   || null,
      muxStreamKey:  _a.muxStreamKey  || null,
      muxRtmpUrl:    _a.muxRtmpUrl    || null,
      muxPlaybackId: _a.muxPlaybackId || null,
      muxStatus:     _a.muxStatus     || null,
    },
    responsive: h.responsive || undefined,
    order: h.order ?? 0, isActive: h.isActive !== false,
  };
}

export function deserializeLobbyHotspot(h) {
  const a = h.action || {};
  let hotspotType = 'link';
  let hotspotAction = {};

  switch (a.type) {
    case 'chat':
      hotspotType = 'chat';
      hotspotAction = { channelId: a.pageId || null, mode: h.meta?.mode || 'group', chatTitle: h.meta?.chatTitle || '', panelWidth: h.meta?.panelWidth || 'md', showParticipantCount: h.meta?.showParticipantCount !== false };
      break;
    case 'modal':
      if (h.meta?.galleryImages) {
        hotspotType = 'gallery';
        hotspotAction = { images: h.meta.galleryImages };
      } else {
        hotspotType = 'iframe';
        hotspotAction = { embedCode: a.url || '', modalWidth: 'md' };
      }
      break;
    case 'stream':
      if (h.meta?._isLivestreamChat) {
        hotspotType = 'livestream-chat';
        hotspotAction = { channelId: h.meta?.channelId || null, chatTitle: h.meta?.chatTitle || 'Livestream Chat' };
      } else {
        hotspotType = 'stream';
        hotspotAction = {
          streamType:    h.meta?.streamType    || 'iframe',
          streamUrl:     h.meta?.streamUrl     || '',
          viewerCount:   h.meta?.viewerCount   || 0,
          muxStreamId:   h.meta?.muxStreamId   || null,
          muxStreamKey:  h.meta?.muxStreamKey  || null,
          muxRtmpUrl:    h.meta?.muxRtmpUrl    || null,
          muxPlaybackId: h.meta?.muxPlaybackId || null,
          muxStatus:     h.meta?.muxStatus     || null,
        };
      }
      break;
    case 'url':
      if (h.meta?._isVideo) {
        hotspotType = 'video';
        hotspotAction = { videoProvider: 'url', videoUrl: a.url || '', autoplay: false, controls: true };
      } else {
        hotspotType = 'link';
        hotspotAction = { linkTarget: 'external', externalUrl: a.url || '', openIn: 'same' };
      }
      break;
    case 'page':
    default:
      hotspotType = 'link';
      hotspotAction = { linkTarget: 'lobby', targetId: a.pageId || null, openIn: 'same', transitionVideoUrl: h.meta?.transitionVideoUrl || null };
      break;
  }

  if (h.meta?._isText || a.type === 'text') {
    hotspotType = 'text';
    hotspotAction = {};
  }

  if (h.meta?._isImage || a.type === 'image') {
    hotspotType = 'image';
    hotspotAction = {
      linkTarget: h.meta?._imageLinkTarget || null,
      targetId:   h.meta?._imageTargetId   || null,
      externalUrl: h.meta?._imageExternalUrl || null,
      transitionVideoUrl: h.meta?._imageTransitionVideoUrl || null,
    };
  }

  return { ...h, hotspotType, hotspotAction };
}

export function serializeLobbyHotspot(h) {
  const { hotspotType, hotspotAction = {}, ...rest } = h;
  let action;
  switch (hotspotType) {
    case 'chat':
      action = { type: 'chat', pageId: hotspotAction.channelId || null, url: null };
      rest.meta = { ...(rest.meta || {}), mode: hotspotAction.mode || 'group', chatTitle: hotspotAction.chatTitle || '', panelWidth: hotspotAction.panelWidth || 'md', showParticipantCount: hotspotAction.showParticipantCount !== false };
      break;
    case 'video':
      action = { type: 'url', pageId: null, url: hotspotAction.videoUrl || null };
      rest.meta = { ...(rest.meta || {}), _isVideo: true, videoProvider: hotspotAction.videoProvider || 'url', autoplay: hotspotAction.autoplay || false, controls: hotspotAction.controls !== false };
      break;
    case 'gallery':
      rest.meta = { ...(rest.meta || {}), galleryImages: hotspotAction.images || [] };
      action = { type: 'modal', pageId: null, url: hotspotAction.images?.[0]?.url || null };
      break;
    case 'iframe':
      action = { type: 'modal', pageId: null, url: hotspotAction.embedCode || null };
      rest.meta = { ...(rest.meta || {}), modalWidth: hotspotAction.modalWidth || 'md' };
      break;
    case 'stream':
      action = { type: 'stream', pageId: null, url: null };
      rest.meta = {
        ...(rest.meta || {}),
        streamType:    hotspotAction.streamType    || 'iframe',
        streamUrl:     hotspotAction.streamUrl     || '',
        viewerCount:   hotspotAction.viewerCount   || 0,
        muxStreamId:   hotspotAction.muxStreamId   || null,
        muxStreamKey:  hotspotAction.muxStreamKey  || null,
        muxRtmpUrl:    hotspotAction.muxRtmpUrl    || null,
        muxPlaybackId: hotspotAction.muxPlaybackId || null,
        muxStatus:     hotspotAction.muxStatus     || null,
      };
      break;
    case 'livestream-chat':
      action = { type: 'stream', pageId: null, url: null };
      rest.meta = { ...(rest.meta || {}), _isLivestreamChat: true, chatTitle: hotspotAction.chatTitle || 'Livestream Chat', channelId: hotspotAction.channelId || null };
      break;
    case 'text':
      action = { type: 'text', pageId: null, url: null };
      rest.meta = { ...(rest.meta || {}), _isText: true };
      break;
    case 'image': {
      action = { type: 'image', pageId: null, url: null };
      const imgMeta = { ...(rest.meta || {}), _isImage: true };
      if (hotspotAction.linkTarget)         imgMeta._imageLinkTarget = hotspotAction.linkTarget;
      else delete imgMeta._imageLinkTarget;
      if (hotspotAction.targetId)           imgMeta._imageTargetId   = hotspotAction.targetId;
      else delete imgMeta._imageTargetId;
      if (hotspotAction.externalUrl)        imgMeta._imageExternalUrl = hotspotAction.externalUrl;
      else delete imgMeta._imageExternalUrl;
      if (hotspotAction.transitionVideoUrl) imgMeta._imageTransitionVideoUrl = hotspotAction.transitionVideoUrl;
      else delete imgMeta._imageTransitionVideoUrl;
      rest.meta = imgMeta;
      break;
    }
    case 'link':
    default: {
      const isExternal = hotspotAction.linkTarget === 'external';
      action = { type: isExternal ? 'url' : 'page', pageId: isExternal ? null : hotspotAction.targetId || null, url: isExternal ? hotspotAction.externalUrl || null : null };
      if (!isExternal && hotspotAction.transitionVideoUrl) {
        rest.meta = { ...(rest.meta || {}), transitionVideoUrl: hotspotAction.transitionVideoUrl };
      } else if (rest.meta) {
        const { transitionVideoUrl: _tv, ...restMeta } = rest.meta;
        rest.meta = restMeta;
      }
      break;
    }
  }
  return { ...rest, action };
}
